<script setup>
import { useStore } from 'vuex';
import { computed, onMounted, ref } from 'vue';
import usePlanPrice from '@/composable/plan-price';
import { menuList } from '@/shared/constants/menu';
import { useRouter } from 'vue-router';
import useLayouts from '@/composable/layout';

const { isCollapse, toggleSidebar } = useLayouts();

const router = useRouter();
const { currentPlan, setPlanPriceDialog } = usePlanPrice();

const store = useStore();
const userRole = computed(() => (permission) => {
  return store.getters['auth/hasPermission'](permission);
});
const ref1 = ref();
const canSeeFeatures = computed(() => {
  return (code) => {
    if (!currentPlan?.value?.features) return false;
    return !!currentPlan?.value?.features?.some((item) => item.code === code);
  };
});

function handleClickProFeature(path, code = '') {
  if (!code) router.push(path);
  else {
    let isAbleToSeeFeature = !!currentPlan?.value?.features?.some((item) => item.code === code);

    if (!isAbleToSeeFeature) setPlanPriceDialog(!isAbleToSeeFeature);
    else router.push(path);
  }
}

function handleSelectMenu(index) {
  toggleSidebar(true);
}

// Recursive filter function
function filterMenu(menu) {
  return menu
    .map((item) => {
      if (item.children) {
        // Check if children are valid
        const filteredChildren = filterMenu(item.children);

        // Include the item if it has valid children or if it has permission
        if (filteredChildren.length || userRole.value(item.permissionCode)) {
          return {
            ...item,
            children: filteredChildren
          };
        }
      } else if (item.groupTitle) {
        // Handle grouped items
        const filteredItems = item.items.filter((child) => userRole.value(child.permissionCode));
        if (filteredItems.length) {
          return {
            ...item,
            items: filteredItems
          };
        }
      } else if (userRole.value(item.permissionCode) || !item.permissionCode) {
        return item;
      }

      return null; // Exclude items that don't match the criteria
    })
    .filter((item) => item !== null);
}

// Computed property to filter menu items
const filteredMenuList = computed(() => filterMenu(menuList));
// Function to detect screen size
const updateCollapseState = () => {
  isCollapse.value = window.innerWidth < 768; // Collapse on small screens (<768px)
};

// Add resize event listener
onMounted(() => {
  updateCollapseState();
  window.addEventListener('resize', updateCollapseState);
  let hasCollapseSaved = localStorage.getItem('isCollapse') === '1';
  if (hasCollapseSaved) {
    toggleSidebar(!!isCollapse);
  }
  // localStorage.getItem('tourTime') ? (open.value = false) : (open.value = true);
  // localStorage.setItem('tourTime', true);
});
</script>

<template>
  <!--  <el-dialog v-model="open" title="🎉 Introducing the New Timeline Feature!" width="800">-->
  <!--    <div class="d-flex">-->
  <!--      We’re excited to announce a brand-new addition to our system: the-->
  <!--      <h5 class="text-info"><i class="ri-bar-chart-horizontal-fill align-bottom mx-2"></i>Timeline</h5>-->
  <!--    </div>-->

  <!--    This feature is designed to help you visualize and track important tasks, and milestones in a clear and organized way.-->

  <!--    <template #footer>-->
  <!--      <div class="dialog-footer">-->
  <!--        <el-button @click="open = false">Close</el-button>-->
  <!--        <el-button type="primary" @click="router.push('/timeline') && (open = false)"> Go to Timeline</el-button>-->
  <!--      </div>-->
  <!--    </template>-->
  <!--  </el-dialog>-->

  <b-card>
    <el-menu
      :collapse="isCollapse"
      active-text-color="#F6A262"
      class="el-menu-vertical-demo border-white"
      collapse-transition
      default-active="1"
      @select="handleSelectMenu"
    >
      <template v-for="item in filteredMenuList" :key="item.index">
        <!-- Submenu -->
        <el-sub-menu v-if="item.children" :index="item.index">
          <template #title>
            <el-icon><i :class="item.icon"></i></el-icon>
            <span class="d-flex justify-content-between align-content-end align-items-center">
              <span>
                {{ $t('menu.' + item.title) }}
              </span>
              <i v-if="item.hasNewFeature" class="bx bxs-diamond fs-18 text-end ms-5 gradient-text" />
            </span>
          </template>

          <!-- Grouped Items -->
          <template v-for="group in item.children" :key="group.groupTitle || group.index">
            <el-menu-item-group v-if="group.groupTitle">
              <el-menu-item
                v-for="groupItem in group.items"
                :key="groupItem.index"
                :index="groupItem.index"
                @click="handleClickProFeature(groupItem.path, item.hasNewFeature)"
              >
                {{ $t('menu.' + groupItem.title) }}
              </el-menu-item>
            </el-menu-item-group>

            <!-- Nested Submenus -->
            <el-sub-menu v-else :index="group.index">
              <template #title>
                <span>{{ group.title }}</span>
              </template>
              <el-menu-item
                v-for="child in group.children"
                :key="child.index"
                :index="child.index"
                @click="handleClickProFeature(child.path, item.hasNewFeature)"
              >
                {{ $t('menu.' + child.title) }}
              </el-menu-item>
            </el-sub-menu>
          </template>
        </el-sub-menu>

        <!-- Regular Menu Items -->
        <el-menu-item
          v-else
          :disabled="item.disabled"
          :index="item.index"
          style="min-width: 60px"
          @click="handleClickProFeature(item.path, item.hasNewFeature)"
        >
          <el-icon><i :class="item.icon"> </i></el-icon>
          <template #title
            ><span class="d-flex justify-content-between align-content-end align-items-center w-100">
              <span>
                {{ $t('menu.' + item.title) }}
              </span>
              <i v-if="item.hasNewFeature" class="bx bxs-diamond fs-18 text-end gradient-text" /> </span
          ></template>
        </el-menu-item>
      </template>
    </el-menu>
  </b-card>
</template>

<style scoped>
.gradient-text {
  background: linear-gradient(135deg, #f06548, #f79d65, #f0c14b);
  -webkit-background-clip: text;
  color: transparent;
}

.el-menu-vertical-demo {
  width: 100%;
  height: 70%;
  border-right: none;
}
</style>
